import LeadCaptureBox from "@/components/LeadCaptureBox";
import EmailGateModal from "@/components/EmailGateModal";
import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { ArrowRight, ChevronRight, Copy, Check, Loader2, RefreshCw, Lock } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";
import { callAIJson } from "@/lib/ai";

// ─── Types ────────────────────────────────────────────────────────────────────
interface GeneratedEmail {
  subject: string;
  body: string;
  followUp: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const FREE_LIMIT = 3;
const STORAGE_KEY = "mm_ceg_uses";

function getUses(): number {
  try { return parseInt(localStorage.getItem(STORAGE_KEY) ?? "0") || 0; } catch { return 0; }
}
function incrementUses(): number {
  const next = getUses() + 1;
  try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* ignore */ }
  return next;
}

const TONE_OPTIONS = ["Professional", "Friendly", "Direct", "Curious"] as const;
type Tone = typeof TONE_OPTIONS[number];

const GOAL_OPTIONS = ["Book a call", "Get a reply", "Demo request", "Partnership"] as const;
type Goal = typeof GOAL_OPTIONS[number];

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(text)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); })
      .catch(() => {});
  }
  return (
    <button type="button" onClick={copy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-black/[0.08] text-slate text-xs font-medium hover:bg-paper transition-colors whitespace-nowrap">
      {copied ? <Check size={12} className="text-teal" /> : <Copy size={12} />}
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

function RadioRow<T extends string>({ options, value, onChange }: { options: readonly T[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)}
          className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${value === o ? "bg-coral/10 border-coral/30 text-coral" : "border-black/[0.08] text-slate hover:border-black/[0.14] hover:text-ink"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ColdEmailGeneratorPage() {
  const [prospectName,    setProspectName]    = useState("");
  const [prospectTitle,   setProspectTitle]   = useState("");
  const [company,         setCompany]         = useState("");
  const [yourProduct,     setYourProduct]     = useState("");
  const [painPoint,       setPainPoint]       = useState("");
  const [tone,            setTone]            = useState<Tone>("Professional");
  const [goal,            setGoal]            = useState<Goal>("Book a call");
  const [loading,         setLoading]         = useState(false);
  const [result,          setResult]          = useState<GeneratedEmail | null>(null);
  const [showGate, setShowGate] = useState(false);
  const [pendingResult, setPendingResult] = useState<GeneratedEmail | null>(null);
  const [error,           setError]           = useState("");
  const [uses,            setUses]            = useState(getUses());
  const resultsRef = useRef<HTMLDivElement>(null);

  const isGated = uses >= FREE_LIMIT;

  function validate() {
    if (!company.trim())      { setError("Please enter the prospect's company name."); return false; }
    if (!yourProduct.trim())  { setError("Please describe what you're offering."); return false; }
    return true;
  }

  async function generate() {
    if (!validate()) return;
    if (isGated) return;
    setError(""); setLoading(true); setResult(null);

    const prompt = `You are a world-class cold email copywriter. Write a highly personalized, concise cold email.

Details:
- Prospect name: ${prospectName || "the prospect"}
- Prospect title: ${prospectTitle || "not specified"}
- Company: ${company}
- What I offer: ${yourProduct}
- Pain point to address: ${painPoint || "general efficiency and growth"}
- Tone: ${tone}
- Goal: ${goal}

Rules:
- Subject: under 50 chars, no clickbait
- Body: 3-4 short paragraphs, under 150 words total, NO generic phrases like "I hope this finds you well"
- First line must hook immediately — reference their company or role specifically
- One clear, low-friction CTA aligned with the goal
- Follow-up: a 1-paragraph follow-up email to send 3 days later if no reply

Return ONLY valid JSON — no markdown:
{"subject":"<subject>","body":"<full email body, use \\n for line breaks>","followUp":"<follow-up email body, use \\n for line breaks>"}`;

    try {
      const p = await callAIJson<Partial<GeneratedEmail>>({ prompt, maxTokens: 1200 });
      setPendingResult({
        subject:   String(p.subject   ?? ""),
        body:      String(p.body      ?? ""),
        followUp:  String(p.followUp  ?? ""),
      });
      const newUses = incrementUses();
      setUses(newUses);
      setShowGate(true);
    } catch (err: unknown) {
      setError("Generation failed: " + (err instanceof Error ? err.message : "Unknown error. Please try again."));
    } finally { setLoading(false); }
  }

  function regenerate() { setResult(null); void generate(); }

  function handleGateComplete() {
    setShowGate(false);
    if (pendingResult) {
      setResult(pendingResult);
      setPendingResult(null);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }

  const usesLeft = Math.max(0, FREE_LIMIT - uses);

  return (
    <>
      {showGate && (
        <EmailGateModal toolName="cold email" source="/cold-email-generator"
          onComplete={handleGateComplete} onClose={() => { setShowGate(false); handleGateComplete(); }} />
      )}
      <SeoHead
        title="Free Cold Email Generator — MoreMail | AI-Written Cold Emails"
        description="Generate personalized cold emails in seconds. Enter prospect details, choose your tone and goal, get a ready-to-send email plus a follow-up. Free, no signup."
        url={`${SITE_URL}/cold-email-generator`}
        breadcrumbs={[
          { name: "Home", url: SITE_URL },
          { name: "Cold Email Generator", url: `${SITE_URL}/cold-email-generator` },
        ]}
      />

      {/* Breadcrumb */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate">
          <Link to="/" className="hover:text-ink transition-colors">Home</Link>
          <ChevronRight size={12} />
          <span className="text-ink">Cold Email Generator</span>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-coral/10 border border-coral/20 rounded-full text-coral text-xs font-bold mb-4">
          Free — {usesLeft} of {FREE_LIMIT} uses remaining
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-ink leading-tight mb-3">
          Cold emails that actually{" "}
          <em className="text-coral not-italic">get replies</em>
        </h1>
        <p className="text-slate text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
          Enter your prospect's details and what you offer. Get a personalized, ready-to-send cold email plus a follow-up — in seconds.
        </p>
      </section>

      {/* Form */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-8">
        <div className="bg-white rounded-2xl border border-black/[0.08] overflow-hidden shadow-sm">

          {/* About the prospect */}
          <div className="px-4 sm:px-5 pt-5 pb-4 border-b border-black/[0.06]">
            <p className="text-xs font-bold uppercase tracking-widest text-slate mb-3">About the prospect</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label htmlFor="ceg-name" className="block text-xs font-medium text-slate mb-1.5">First name</label>
                <input id="ceg-name" type="text" value={prospectName} onChange={(e) => setProspectName(e.target.value)}
                  placeholder="Sarah" maxLength={60}
                  className="w-full bg-paper rounded-xl border border-black/[0.08] px-3 py-2.5 text-sm text-ink placeholder-slate/40 focus:outline-none focus:border-coral/40 focus:ring-2 focus:ring-coral/10 transition" />
              </div>
              <div>
                <label htmlFor="ceg-title" className="block text-xs font-medium text-slate mb-1.5">Job title</label>
                <input id="ceg-title" type="text" value={prospectTitle} onChange={(e) => setProspectTitle(e.target.value)}
                  placeholder="Head of Marketing" maxLength={80}
                  className="w-full bg-paper rounded-xl border border-black/[0.08] px-3 py-2.5 text-sm text-ink placeholder-slate/40 focus:outline-none focus:border-coral/40 focus:ring-2 focus:ring-coral/10 transition" />
              </div>
              <div>
                <label htmlFor="ceg-company" className="block text-xs font-medium text-slate mb-1.5">Company <span className="text-coral">*</span></label>
                <input id="ceg-company" type="text" value={company} onChange={(e) => { setCompany(e.target.value); setError(""); }}
                  placeholder="Acme Corp" maxLength={80}
                  className="w-full bg-paper rounded-xl border border-black/[0.08] px-3 py-2.5 text-sm text-ink placeholder-slate/40 focus:outline-none focus:border-coral/40 focus:ring-2 focus:ring-coral/10 transition" />
              </div>
            </div>
          </div>

          {/* About your offer */}
          <div className="px-4 sm:px-5 pt-4 pb-4 border-b border-black/[0.06]">
            <p className="text-xs font-bold uppercase tracking-widest text-slate mb-3">Your offer</p>
            <div className="space-y-3">
              <div>
                <label htmlFor="ceg-product" className="block text-xs font-medium text-slate mb-1.5">What you're offering <span className="text-coral">*</span></label>
                <input id="ceg-product" type="text" value={yourProduct} onChange={(e) => { setYourProduct(e.target.value); setError(""); }}
                  placeholder="e.g. AI email warmup tool that improves inbox placement by 40%"
                  maxLength={200}
                  className="w-full bg-paper rounded-xl border border-black/[0.08] px-3 py-2.5 text-sm text-ink placeholder-slate/40 focus:outline-none focus:border-coral/40 focus:ring-2 focus:ring-coral/10 transition" />
              </div>
              <div>
                <label htmlFor="ceg-pain" className="block text-xs font-medium text-slate mb-1.5">Pain point to address <span className="text-slate/40 font-normal">(optional)</span></label>
                <input id="ceg-pain" type="text" value={painPoint} onChange={(e) => setPainPoint(e.target.value)}
                  placeholder="e.g. cold emails landing in spam, low reply rates"
                  maxLength={200}
                  className="w-full bg-paper rounded-xl border border-black/[0.08] px-3 py-2.5 text-sm text-ink placeholder-slate/40 focus:outline-none focus:border-coral/40 focus:ring-2 focus:ring-coral/10 transition" />
              </div>
            </div>
          </div>

          {/* Tone + Goal */}
          <div className="px-4 sm:px-5 pt-4 pb-4 border-b border-black/[0.06]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate mb-2">Tone</p>
                <RadioRow<Tone> options={TONE_OPTIONS} value={tone} onChange={setTone} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate mb-2">Goal</p>
                <RadioRow<Goal> options={GOAL_OPTIONS} value={goal} onChange={setGoal} />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="px-4 sm:px-5 py-3.5 bg-paper flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs text-slate/60">
              {usesLeft > 0
                ? <>{usesLeft} free generation{usesLeft !== 1 ? "s" : ""} remaining</>
                : <span className="text-amber-600 font-medium flex items-center gap-1"><Lock size={11} /> Free limit reached — sign up for unlimited</span>}
            </span>
            <button type="button" onClick={generate} disabled={loading || isGated}
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 bg-coral text-white text-sm font-semibold rounded-full hover:bg-coral-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md shadow-coral/20 whitespace-nowrap">
              {loading ? <><Loader2 size={14} className="animate-spin flex-shrink-0" />Writing email...</> : <>Generate cold email <ArrowRight size={14} /></>}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && <div role="alert" className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>}

        {/* Gate wall */}
        {isGated && !result && (
          <div className="mt-4 bg-ink rounded-2xl px-5 sm:px-6 py-6 text-center">
            <div className="w-10 h-10 rounded-full bg-coral/20 flex items-center justify-center mx-auto mb-3">
              <Lock size={18} className="text-coral" />
            </div>
            <h3 className="font-display text-lg font-bold text-white mb-1">You've used all {FREE_LIMIT} free generations</h3>
            <p className="text-sm text-white/50 mb-4">Sign up free to generate unlimited cold emails, run warmup, and track campaigns.</p>
            <LeadCaptureBox />
          </div>
        )}
      </section>

      {/* Results */}
      {result && (
        <section ref={resultsRef} className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">

          {/* Subject line */}
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden mb-4">
            <div className="px-4 sm:px-5 py-3.5 border-b border-black/[0.06] bg-paper flex items-center justify-between gap-3">
              <p className="text-xs font-bold uppercase tracking-widest text-slate">Subject line</p>
              <CopyBtn text={result.subject} />
            </div>
            <div className="px-4 sm:px-5 py-4">
              <p className="text-base font-semibold text-ink">{result.subject}</p>
              <p className={`text-xs mt-1.5 font-mono ${result.subject.length <= 50 ? "text-teal" : "text-amber-500"}`}>
                {result.subject.length} chars — {result.subject.length <= 50 ? "ideal length" : "consider shortening"}
              </p>
            </div>
          </div>

          {/* Email body */}
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden mb-4">
            <div className="px-4 sm:px-5 py-3.5 border-b border-black/[0.06] bg-paper flex items-center justify-between gap-3">
              <p className="text-xs font-bold uppercase tracking-widest text-slate">Email body</p>
              <CopyBtn text={result.body} />
            </div>
            <div className="px-4 sm:px-5 py-4">
              <div className="text-sm text-ink leading-relaxed whitespace-pre-line">{result.body}</div>
              <div className="mt-3 pt-3 border-t border-black/[0.04] flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs text-slate/60 font-mono tabular-nums">
                  {result.body.split(/\s+/).filter(Boolean).length} words
                </span>
                <span className={`text-xs font-medium ${result.body.split(/\s+/).filter(Boolean).length <= 150 ? "text-teal" : "text-amber-500"}`}>
                  {result.body.split(/\s+/).filter(Boolean).length <= 150 ? "✓ Under 150 words — good length" : "Consider trimming for better reply rates"}
                </span>
              </div>
            </div>
          </div>

          {/* Follow-up */}
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden mb-4">
            <div className="px-4 sm:px-5 py-3.5 border-b border-black/[0.06] bg-paper flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate">3-day follow-up</p>
                <p className="text-[10px] text-slate/60 mt-0.5">Send if no reply after 3 days</p>
              </div>
              <CopyBtn text={result.followUp} />
            </div>
            <div className="px-4 sm:px-5 py-4">
              <div className="text-sm text-ink leading-relaxed whitespace-pre-line">{result.followUp}</div>
            </div>
          </div>

          {/* Action row */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <button type="button" onClick={regenerate} disabled={loading || isGated}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-black/[0.08] text-slate text-sm font-semibold rounded-full hover:bg-paper hover:border-black/[0.14] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <RefreshCw size={14} />{loading ? "Regenerating..." : "Regenerate"}
            </button>
            {usesLeft > 0 && (
              <p className="flex-1 flex items-center justify-center text-xs text-slate/60">{usesLeft} free generation{usesLeft !== 1 ? "s" : ""} left</p>
            )}
          </div>

          {/* Gate after last use */}
          {isGated && (
            <div className="bg-ink rounded-2xl px-5 sm:px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div>
                <h3 className="font-display text-lg sm:text-xl font-bold text-white mb-1">You've used your {FREE_LIMIT} free emails</h3>
                <p className="text-sm text-white/50">Sign up free for unlimited generation + full campaign sending.</p>
              </div>
              <LeadCaptureBox />
            </div>
          )}

          {/* Tips panel */}
          <div className="bg-coral/5 border border-coral/15 rounded-2xl px-4 sm:px-5 py-4">
            <p className="text-xs font-bold uppercase tracking-widest text-coral mb-2">Pro tips</p>
            <ul className="space-y-1.5">
              {[
                "Personalize the first line further with a recent company news or LinkedIn post.",
                "Send between 7–9am recipient's local time for best open rates.",
                "Keep the subject out of the spam folder — run it through the Subject Line Tester.",
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-slate leading-relaxed">
                  <span className="text-coral font-bold flex-shrink-0 mt-px">{i + 1}.</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Social proof */}
      {!result && !isGated && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
            {[
              { stat: "8×", label: "more replies vs templates" },
              { stat: "<150", label: "words — ideal length" },
              { stat: "Free", label: `${FREE_LIMIT} emails, no signup` },
            ].map(({ stat, label }) => (
              <div key={label} className="bg-white rounded-2xl border border-black/[0.06] py-4 sm:py-5">
                <div className="font-display text-xl sm:text-2xl font-bold text-coral">{stat}</div>
                <div className="text-xs text-slate mt-1">{label}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
